<template>
  <Loader
    :value="promise"
    :message="$t('loading', { resource: $tc('lead', 2) }) + '...'"
    :class="['project-leads', `project-leads--view-${view}`, `project-leads--variant-${variant}`]"
  >
    <div :class="['view', { container: view === 'list' }]">
      <HeaderComponent
        :title="$metaInfo.title"
        :breadcrumb="breadcrumb"
        :class="{ container: view === 'kanban' }"
        :sticky="false"
      >
        <template #actions>
          <Actions placement="leads" :query="{ filter: { ...filter, references: [reference] }, sort }" />

          <DropdownComponent :closeOnClick="false">
            <ButtonComponent :label="$t('add')" :icon="['fal', 'plus']" theme="primary" />
            <template #dropdown="{ close, visible }">
              <ButtonComponent
                tag="router-link"
                :to="{
                  name: `${context}-lead-create`,
                  params: { reference },
                  query: { returnTo: $route.path },
                }"
                :label="$tc('lead')"
                :icon="['fal', 'plus']"
              />
              <ButtonComponent
                tag="router-link"
                :label="$tc('import')"
                :to="{
                  name: 'import',
                  params: { type: 'lead' },
                  query: { returnTo: $route.path, ref: reference, onModel },
                }"
                :icon="['fal', 'file-import']"
                @click="close"
              />
            </template>
          </DropdownComponent>
        </template>
      </HeaderComponent>
      <div :class="['project-leads__filter', { container: view === 'kanban' }]">
        <FilterComponent v-model="filter" v-bind="filterOptions">
          <template #status="{ items }">{{
            items.length ? items.map(i => getColumnById(i).label).join(', ') : $t('all')
          }}</template>
        </FilterComponent>
        <SortComponent v-if="view === 'list'" v-model="sort" :items="sortableBy" />
        <DropdownComponent v-if="view === 'kanban'" :closeOnClick="false">
          <ButtonComponent icon="fa-pro-light:cog" />
          <template #dropdown="{ close, visible }">
            <ButtonComponent
              v-if="view === 'kanban' && $store.getters.isAdmin"
              tag="router-link"
              :label="$tc('kanbanPipelinesLabel', 1)"
              icon="fa-pro-regular:square-kanban"
              :to="{ name: 'settings', params: { step: 'accountsettings' } }"
            />
            <strong>{{ $t('show') }}:</strong>
            <ChecklistComponent v-model="visibleProperties" :items="visiblePropertiesItems" class="px-2 pb-2" />
          </template>
        </DropdownComponent>
        <ButtonGroup class="project-leads__view-toggle">
          <ButtonComponent
            v-for="v in views"
            :icon="v.icon"
            :theme="v.value === view ? 'secondary' : 'default'"
            @click="view = v.value"
          />
        </ButtonGroup>
      </div>

      <component :is="viewOptions.component" v-bind="viewOptions.props" v-on="viewOptions.listeners" />

      <LeadModal />
    </div>
  </Loader>
</template>

<script>
import { Debounce } from '@/utils'
import StorageMixin from '@/mixins/Storage'
import FilterMixin from '@/components/Filter'
import API from '@/api/Project'

import graphql from '@/api/graphql'

import { DropdownComponent } from 'vue-elder-dropdown'
import ChecklistComponent from '@/components/Select/Checklist'
import HeaderComponent from '@/components/Header'
import SortComponent, { SortLocal, getSort } from '@/components/Sort'
import ProjectCard from '@/components/Project/Card'
import Actions from '@/components/Actions'
import Card from '@/components/Card'
import ButtonGroup from '@kvass/button-group'
import LeadModal from '@/components/Lead/Modal'

import EmptyState from '@/components/EmptyState'
import Views from './views'
import { getPipeline } from '@/components/Kanban/Component.vue'
import { LeadPathByModel, getListLabel } from '@/components/Lead/utils'

import tracker from '@/tracking'

export default {
  name: 'ProjectLeads',
  mixins: [
    FilterMixin({
      default: { search: '', residentials: undefined, includeArchived: false },
    }),
    StorageMixin({
      target: 'view',
      key: 'leads-view',
      storage: 'localStorage',
      disabled() {
        return this.$attrs.view
      },
    }),
    StorageMixin({
      target: 'visibleProperties',
      key: 'leads-visibleProperties',
      storage: 'localStorage',
    }),
  ],
  props: {
    reference: String,
    onModel: {
      type: String,
      default: 'Project',
    },
    variant: {
      type: String,
      default: 'default',
    },
  },
  watch: {
    filter: {
      handler() {
        if (!this.promise) return this.fetch('filter')

        this.debouncedFetch('filter')
      },
      deep: true,
    },
    sort: {
      handler() {},
      deep: true,
    },
  },
  data() {
    return {
      item: {},
      sort: { 'contact.name': 1 },
      promise: null,
      view: 'kanban',
      views: [
        { value: 'list', label: this.$t('list'), icon: 'fa-pro-regular:list-ul' },
        { value: 'kanban', label: this.$t('kanban'), icon: 'fa-pro-regular:square-kanban' },
      ],
      visibleProperties: ['lastActivity', 'firstActivity', 'referrer'],
      visiblePropertiesItems: [
        { value: 'lastActivity', label: this.$t('lastActivity') },
        { value: 'firstActivity', label: this.$t('firstActivity') },
        { value: 'referrer', label: this.$tc('leadReferrer', 1) },
        { value: 'tags', label: this.$tc('tag', 2) },
      ],
      sortableBy: [
        { key: 'contact.email', label: this.$t('email'), virtual: true },
        { key: 'contact.name', label: this.$t('name'), virtual: true },
        { key: 'createdAt', label: this.$t('createdAt'), virtual: true },
      ],
      exportItems: [
        { value: 'name', label: this.$t('name') },
        { value: 'email', label: this.$t('email') },
        { value: 'type', label: this.$tc('type', 1) },
        { value: 'phone', label: this.$t('phone') },
        { value: 'tags', label: this.$tc('tag', 2) },
        { value: 'createdAt', label: this.$t('createdAt') },
      ],
      unsortedItems: [],
    }
  },
  computed: {
    context() {
      return LeadPathByModel[this.onModel]
    },
    isProject() {
      return this.onModel === 'Project'
    },
    pipeline() {
      return getPipeline(this.reference, this.$store.state.enums.KanbanSettings.pipelines)
    },
    filterOptions() {
      return {
        default: this.defaultFilter,
        options: {
          residentialTemplates: { label: this.$tc('unitCatalog') },
          scopes: { filter: { ids: [this.reference] }, label: this.$tc('unit', 2) },
          includeArchived: { label: this.$t('showArchived') },
          contactTypes: { label: this.$tc('type', 1) },
          status: {
            label: this.$t('status'),
            items: this.pipeline.columns.map(c => ({ value: c.id || 'default', label: c.label })),
          },
          tags: { items: `/api/${this.context}/${this.reference}/lead/tags` },
        },
        items: [
          'search',
          this.view === 'list' && 'checklist:status',
          this.isProject && 'residentials:scopes',
          this.isProject && 'residentialTemplates',
          'tags',
          'types:contactTypes',
          'checkbox:includeArchived',
        ].filter(Boolean),
      }
    },
    viewOptions() {
      return {
        component: Views[this.view],
        props: {
          items: this.items,
          sort: this.sort,
          sortableBy: this.sortableBy,
          reference: this.reference,
          onModel: this.onModel,
          pipeline: this.pipeline,
          visibleProperties: this.visibleProperties,
        },
        listeners: {
          'update:sort': e => (this.sort = e),
          change: ({ target, value, sort }) => {
            const data = {}

            if (target.status !== value) {
              data.status = value
              target.status = value
            }

            if (sort) {
              data.priority = sort
              target.priority = sort
            }

            graphql.request(`mutation($id: ID!, $data: LeadUpdate!){ LeadUpdate(id: $id, data: $data) { id }}`, {
              id: target.id,
              data,
            })
          },
          status,
        },
      }
    },
    breadcrumb() {
      return [
        { title: getListLabel(this.onModel), action: { name: `${this.context}s` } },
        {
          title: this.item.name,
          action: { name: `${this.context}-dashboard`, params: { id: this.reference } },
        },
        { title: this.$metaInfo.title },
      ]
    },

    filteredItems() {
      return this.unsortedItems.filter(item => this.filter.includeArchived || !item.isArchived)
    },
    sortedItems: SortLocal('filteredItems'),
    items() {
      if (this.view === 'list') return this.sortedItems
      return this.filteredItems.sort((a, b) => (this.view === 'list' ? 0 : b.priority - a.priority))
    },
  },
  methods: {
    getColumnById(id) {
      if (id === 'default') id = null
      return this.pipeline.columns.find(c => c.id === id)
    },
    fetch(v) {
      if (!this.reference) return Promise.resolve()
      if (v) {
        if (Object.values(this[v]).filter(v => (Array.isArray(v) ? v.length : Boolean(v))).length)
          tracker.emit(`${this.onModel}Leads:${v}`, { ...this[v] })
      }

      this.promise = API.get(
        {
          reference: this.reference,
          filter: this.filter,
        },
        {
          query: `${this.onModel}Leads`,
        },
      ).then(res => {
        const { Leads } = res
        this.item = res[this.onModel]

        this.unsortedItems = Leads.map((c, i) => ({
          ...c,
          priority: c.priority || i + 1001,
        }))

        return this.unsortedItems
      })

      return this.promise
    },

    debouncedFetch: Debounce(function (v) {
      this.fetch(v)
    }, 400),
  },
  mounted() {
    if (this.$attrs.view) this.view = this.$attrs.view
  },
  metaInfo() {
    return {
      title: this.$tc('lead', 2),
    }
  },
  components: {
    ProjectCard,
    Card,
    HeaderComponent,
    SortComponent,
    DropdownComponent,
    Actions,
    EmptyState,
    ButtonGroup,
    LeadModal,
    ChecklistComponent,
  },
}
</script>

<style lang="scss">
.project-leads {
  background-color: $background-grey;

  &--variant-kanban-embed {
    header,
    .project-leads__view-toggle {
      display: none;
    }

    .view,
    .container {
      padding: 0;
      max-width: initial;
    }

    .kanban {
      padding: 0 !important;
    }
  }

  &__filter {
    display: flex;
    align-items: flex-end;
    gap: 1rem;

    margin-bottom: 2rem;

    button {
      min-height: 50px;
    }

    .filter {
      margin-right: auto;
    }
  }

  .kanban {
    max-width: calc(100vw - 4rem);

    margin: 0 auto;
  }
}
</style>
